import { auth } from "@/app/auth/actions";
import publicConfig from "@/config/public.config";
import { createClient } from "@/utils/supabase/client";
import {
  SigninFormData,
  signinSchema,
  SignupFormData,
  signupSchema,
} from "./schema";

type SigninResult = {
  errors: Partial<SignupFormData> | null;
};

export async function signin(
  data: SigninFormData,
): Promise<SigninResult> {
  const supabase = await createClient();

  const validatedFields = signinSchema.safeParse(data);

  if (!validatedFields.success) {
    const errors: Partial<SigninFormData> = {};
    validatedFields.error.issues.forEach((issue) => {
      const path = issue.path[0] as string;
      if (path === "email" || path === "password") {
        errors[path] = issue.message;
      }
    });

    return {
      errors,
    };
  }

  const { error } = await supabase.auth.signInWithPassword(
    validatedFields.data,
  );

  if (error) {
    return {
      errors: {
        email: error.message,
      },
    };
  }

  return {
    errors: null,
  };
}

type SignupResult =
  | { success: true }
  | {
    success: false;
    errors?: Partial<SignupFormData>;
  };

export async function signup(
  data: SignupFormData,
): Promise<SignupResult> {
  const supabase = await createClient();

  const validatedFields = signupSchema.safeParse(data);

  if (!validatedFields.success) {
    const errors: Partial<SignupFormData> = {};
    validatedFields.error.issues.forEach((issue) => {
      const path = issue.path[0] as string;
      if (path === "email" || path === "password") {
        errors[path] = issue.message;
      }
    });

    return {
      success: false,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  }

  const { email, password } = validatedFields.data;

  const { claims } = await auth();

  if (claims?.is_anonymous) {
    const { error: updateError } = await supabase.auth.updateUser({
      email,
      password,
    });

    if (updateError) {
      return {
        success: false,
        errors: {
          email: updateError.message,
        },
      };
    }

    const { error: updateProfileError } = await supabase.from("profiles")
      .update({
        email,
      }).eq("id", claims?.sub);

    if (updateProfileError) {
      return {
        success: false,
        errors: {
          email: updateProfileError.message,
        },
      };
    }

    const { errors } = await signin({ email, password });

    if (errors) {
      return {
        success: false,
        errors,
      };
    }

    return {
      success: true,
    };
  }

  const { data: signUpData, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${publicConfig.baseUrl}/auth/callback`,
    },
  });

  if (error) {
    if (error.message.includes("Password")) {
      return {
        success: false,
        errors: {
          password: "Password does not meet requirements",
        },
      };
    }

    return {
      success: false,
      errors: {
        email: error.message,
      },
    };
  }

  if (signUpData?.user?.id && signUpData.user.email) {
    const { sendVerificationEmail } = await import("@/app/auth/actions");
    await sendVerificationEmail(
      signUpData.user.id,
      signUpData.user.email,
    );
  }

  return {
    success: true,
  };
}
